import { useState, useCallback } from "react";
import { useSnackbar } from "notistack";

export interface ContextMenuState {
  mouseX: number;
  mouseY: number;
  coordinates: [number, number];
}

/**
 * useContextMenu
 *
 * Manages a right-click context menu for the map.
 *
 * Responsibilities:
 * - Capture screen position and geographic coordinates on right-click
 * - Prevent MapLibre and browser default context menu behavior
 * - Expose a simple API for opening, closing, and interacting with the menu
 * - Provide a utility to copy coordinates with user feedback
 *
 * Notes:
 * - Event propagation is explicitly stopped to avoid MUI Menu
 *   closing immediately due to map click events.
 * - Designed to be UI-agnostic; rendering is handled by the caller.
 **/

export const useContextMenu = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);

  /**
   * Handles right-click events on the map.
   * Prevents default browser and map interactions and
   * stores the click position and coordinates.
   */
  const handleMapRightClick = useCallback((e: any) => {
    e.preventDefault();
    e.originalEvent?.preventDefault();
    e.originalEvent?.stopPropagation();

    const { lngLat, point } = e;

    setContextMenu({
      mouseX: point.x,
      mouseY: point.y,
      coordinates: [lngLat.lng, lngLat.lat]
    });
  }, []);

  // function to close the context menu
  const handleClose = useCallback(() => {
    setContextMenu(null);
  }, []);

  /**
   * Copies the clicked coordinates to the clipboard and
   * shows a feedback message to the user.
   *
   * Falls back to a manual copy mechanism if the Clipboard API
   * is not available.
   */
  const handleCopyCoordinates = async () => {
    if (!contextMenu) return;

    const [lng, lat] = contextMenu.coordinates;
    const coordinatesText = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

    try {
      await navigator.clipboard.writeText(coordinatesText);
      enqueueSnackbar(`Coordinates copied: ${coordinatesText}`, {
        variant: "success",
        autoHideDuration: 3000
      });
    } catch {
      // Fallback for browsers that don't support the Clipboard API
      const textArea = document.createElement("textarea");
      textArea.value = coordinatesText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);

      enqueueSnackbar(`Coordinates copied: ${coordinatesText}`, {
        variant: "success",
        autoHideDuration: 3000
      });
    }

    handleClose();
  };

  return {
    contextMenu,
    isOpen: contextMenu !== null,
    handleMapRightClick,
    handleClose,
    handleCopyCoordinates
  };
};
